import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  AlertCircle, Plus, Loader2, Clock, CheckCircle2,
  MessageSquare, ChevronRight, Star, Send
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'civil', label: 'Civil Work' },
  { value: 'carpentry', label: 'Carpentry' },
  { value: 'painting', label: 'Painting' },
  { value: 'flooring', label: 'Flooring' },
  { value: 'waterproofing', label: 'Waterproofing' },
  { value: 'hvac', label: 'AC/HVAC' },
  { value: 'security', label: 'Security' },
  { value: 'common_area', label: 'Common Area' },
  { value: 'other', label: 'Other' }
];

const priorities = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' }
];

const statusColors = {
  open: 'bg-yellow-100 text-yellow-700',
  assigned: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-purple-100 text-purple-700',
  pending_buyer: 'bg-orange-100 text-orange-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-700',
  reopened: 'bg-red-100 text-red-700'
};

export default function BuyerComplaintsPage() {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [complaintDetail, setComplaintDetail] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [newComplaint, setNewComplaint] = useState({
    title: '',
    description: '',
    category: 'other',
    priority: 'medium'
  });

  const buyerData = JSON.parse(localStorage.getItem('buyerData') || '{}');
  const buyerId = buyerData?.id;

  useEffect(() => {
    if (buyerId) loadComplaints();
  }, [buyerId, filter]);

  const loadComplaints = async () => {
    try {
      const token = localStorage.getItem('buyerToken');
      let url = `${API}/api/buyer-portal/complaints?buyer_id=${buyerId}`;
      if (filter !== 'all') {
        url += `&status=${filter}`;
      }
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setComplaints(data);
      }
    } catch (error) {
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  const loadComplaintDetail = async (complaintId) => {
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/complaints/${complaintId}?buyer_id=${buyerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setComplaintDetail(data);
        setShowDetailDialog(true);
      }
    } catch (error) {
      toast.error('Failed to load complaint details');
    }
  };

  const handleCreateComplaint = async () => {
    if (!newComplaint.title || !newComplaint.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/complaints?buyer_id=${buyerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newComplaint)
      });

      if (res.ok) {
        toast.success('Complaint submitted successfully');
        setShowNewDialog(false);
        setNewComplaint({ title: '', description: '', category: 'other', priority: 'medium' });
        loadComplaints();
      } else {
        throw new Error('Failed to create complaint');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/complaints/${complaintDetail.complaint.id}/comment?buyer_id=${buyerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ comment: newComment })
      });

      if (res.ok) {
        setNewComment('');
        loadComplaintDetail(complaintDetail.complaint.id);
        toast.success('Comment added');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFeedback = async (rating) => {
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/complaints/${complaintDetail.complaint.id}/feedback?buyer_id=${buyerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ rating, feedback: '' })
      });

      if (res.ok) {
        toast.success('Thank you for your feedback!');
        setShowDetailDialog(false);
        loadComplaints();
      }
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Complaints & Tickets</h1>
          <p className="text-muted-foreground">Report and track issues with your property</p>
        </div>
        <Button onClick={() => setShowNewDialog(true)} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="h-4 w-4 mr-2" />
          New Complaint
        </Button>
      </div>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'open', 'in_progress', 'resolved', 'closed'].map((status) => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(status)}
            className={filter === status ? 'bg-amber-600' : ''}
          >
            {status === 'all' ? 'All' : status.replace('_', ' ')}
          </Button>
        ))}
      </div>

      {/* Complaints List */}
      {complaints.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium text-lg mb-2">No complaints yet</h3>
            <p className="text-muted-foreground mb-4">
              {filter === 'all' 
                ? 'You have not raised any complaints'
                : `No ${filter.replace('_', ' ')} complaints`}
            </p>
            <Button onClick={() => setShowNewDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Raise a Complaint
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {complaints.map((complaint) => (
            <Card 
              key={complaint.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => loadComplaintDetail(complaint.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium">{complaint.title}</h3>
                      <Badge className={statusColors[complaint.status] || 'bg-gray-100'}>
                        {complaint.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {complaint.description}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="capitalize">{complaint.category}</span>
                      <span>•</span>
                      <Badge variant="outline" className={priorities.find(p => p.value === complaint.priority)?.color}>
                        {complaint.priority}
                      </Badge>
                      <span>•</span>
                      <span>{new Date(complaint.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Complaint Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Complaint</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                placeholder="Brief description of the issue"
                value={newComplaint.title}
                onChange={(e) => setNewComplaint({ ...newComplaint, title: e.target.value })}
                data-testid="input-complaint-title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={newComplaint.category}
                  onValueChange={(v) => setNewComplaint({ ...newComplaint, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={newComplaint.priority}
                  onValueChange={(v) => setNewComplaint({ ...newComplaint, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                placeholder="Provide detailed description of the issue..."
                rows={4}
                value={newComplaint.description}
                onChange={(e) => setNewComplaint({ ...newComplaint, description: e.target.value })}
                data-testid="input-complaint-description"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateComplaint} 
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Complaint
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complaint Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {complaintDetail?.complaint?.title}
              <Badge className={statusColors[complaintDetail?.complaint?.status]}>
                {complaintDetail?.complaint?.status?.replace('_', ' ')}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          {complaintDetail && (
            <div className="space-y-4">
              <div className="flex gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Category:</span>{' '}
                  <span className="capitalize">{complaintDetail.complaint.category}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Priority:</span>{' '}
                  <Badge variant="outline" className={priorities.find(p => p.value === complaintDetail.complaint.priority)?.color}>
                    {complaintDetail.complaint.priority}
                  </Badge>
                </div>
                <div>
                  <span className="text-muted-foreground">Created:</span>{' '}
                  {new Date(complaintDetail.complaint.created_at).toLocaleDateString()}
                </div>
              </div>

              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm">{complaintDetail.complaint.description}</p>
              </div>

              {/* Resolution Notes */}
              {complaintDetail.complaint.resolution_notes && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm font-medium text-green-800 mb-1">Resolution</p>
                  <p className="text-sm text-green-700">{complaintDetail.complaint.resolution_notes}</p>
                </div>
              )}

              {/* Comments */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Comments ({complaintDetail.comments?.length || 0})
                </h4>
                
                <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                  {complaintDetail.comments?.map((comment) => (
                    <div key={comment.id} className={`p-3 rounded-lg ${comment.commented_by_role === 'buyer' ? 'bg-amber-50 ml-8' : 'bg-blue-50 mr-8'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{comment.commented_by_name}</span>
                        <Badge variant="outline" className="text-xs">{comment.commented_by_role}</Badge>
                      </div>
                      <p className="text-sm">{comment.comment}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(comment.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                  {(!complaintDetail.comments || complaintDetail.comments.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">No comments yet</p>
                  )}
                </div>

                {/* Add Comment */}
                {!['closed', 'resolved'].includes(complaintDetail.complaint.status) && (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                    />
                    <Button onClick={handleAddComment} disabled={submitting || !newComment.trim()}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Feedback for resolved complaints */}
              {complaintDetail.complaint.status === 'resolved' && !complaintDetail.complaint.buyer_rating && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Rate this resolution</h4>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <Button
                        key={rating}
                        variant="outline"
                        size="sm"
                        onClick={() => handleSubmitFeedback(rating)}
                      >
                        <Star className={`h-4 w-4 ${rating <= 3 ? 'text-yellow-500' : 'text-yellow-500'}`} />
                        {rating}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {complaintDetail.complaint.buyer_rating && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Your rating:</span>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star 
                        key={i} 
                        className={`h-4 w-4 ${i <= complaintDetail.complaint.buyer_rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
